import { useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc'
// Models
import IEventTime from 'app/models/EventTime';
import { ICurrentAccount } from 'app/models/Account';
// Redux
import { useAppSelector } from './useStore';
// Selectors
import { selectCurrentAccount } from 'app/store/Accounts/Accounts.selectors';

type Props = {
  dateValue?: string | undefined;
  timeValue?: IEventTime | string | undefined;
  displayTime?: boolean;
  local?: boolean
}

// @date: YYYY-MM-DDTHH:mm:ss.zzzZ || YYYY-MM-DD
// @time: HH:mm

/**
 * React hook to format date/time using current account's time format settings.
 * Returns [formattedValue, formatterFunction].
 */
export default function useLongDateTime({ dateValue, timeValue, displayTime = false, local = false }:Props): [string | undefined, (date: string | undefined, time?: IEventTime | string | undefined) => string | undefined]{
  const currentAccount:ICurrentAccount | null = useAppSelector(selectCurrentAccount);
  const timeFormat = currentAccount?.settings?.timeFormat || 'hh:mm a';

  const formatDateTime = useCallback((date: string | undefined, time?: IEventTime | string | undefined): string | undefined => {
    if ( !date ) return undefined;

    const timeToUse = time !== undefined ? time : timeValue;
    const initialDateFormat = 'MMMM DD, YYYY';
    const initialTimeFormat = timeFormat;

    const dateTimeFormat = displayTime || timeToUse
      ? `${initialDateFormat}, ${initialTimeFormat}`
      : initialDateFormat
    ;

    let dayJsDate;
    if ( local ){
      dayjs.extend(utc);
      dayJsDate = dayjs(date).local();
    } else {
      dayJsDate = dayjs(date);
    }

    if ( timeToUse ){
      let hour;
      let minute;
      if ( typeof timeToUse === 'string' ){
        hour = Number(timeToUse.split(':')[0]);
        minute = Number(timeToUse.split(':')[1]);
      } else {
        hour = timeToUse.hour;
        minute = timeToUse.minute;
      }
      return dayJsDate.hour(hour).minute(minute).format(dateTimeFormat);
    } else {
      return dayJsDate.format(dateTimeFormat);
    }
    // eslint-disable-next-line
  }, [timeValue, displayTime, local, timeFormat]);

  const formattedDateTime = useMemo(() => {
    return formatDateTime(dateValue, timeValue);
  }, [dateValue, timeValue, formatDateTime]);

  return [formattedDateTime, formatDateTime];
}
